// src/pages/Blog.jsx
// Redesigned — Magazine-style layout matching MetaVix site colours & fonts
import React, { useState, useEffect } from "react";

// ── CONFIGURATION ─────────────────────────────────────────────────────────────
const WP_API = "https://cms.metavixsolutions.com/wp-json/wp/v2";

// ── SITE DESIGN TOKENS ────────────────────────────────────────────────────────
const S = {
  gradientBg:  "linear-gradient(135deg,#1e3a8a 0%,#1e40af 60%,#1d4ed8 100%)",
  blue:        "#1d4ed8",
  blueDark:    "#1e3a8a",
  blueMid:     "#1e40af",
  red:         "#dc2626",
  redHover:    "#b91c1c",
  textDark:    "#1e293b",
  textMid:     "#374151",
  textLight:   "#64748b",
  textMuted:   "#94a3b8",
  border:      "#e2e8f0",
  bg:          "#f8fafc",
  card:        "#ffffff",
  font:        "'Montserrat', 'Open Sans', system-ui, sans-serif",
};

// ── SIDEBAR CATEGORIES ────────────────────────────────────────────────────────
const sidebarCategories = [
  { name: "Oracle Latest Updates", slug: "oracle-updates" },
  {
    name: "Oracle Cloud",
    slug: "Oracle Cloud",
    children: [
      { name: "Oracle ERP",   slug: "Oracle ERP" },
      { name: "Oracle SCM",   slug: "Oracle SCM" },
      { name: "Oracle HCM",   slug: "Oracle HCM" },
      { name: "AI Agents",    slug: "AI Agents" },
    ],
  },
  {
    name: "Oracle Analytics",
    slug: "Oracle Analytics",
    children: [
      { name: "Oracle FDI/OAC", slug: "Oracle FDI/OAC" },
    ],
  },
  { name: "AI Latest Updates", slug: "AI Latest Updates" },
  {
    name: "Artificial Intelligence",
    slug: "Artificial Intelligence",
    children: [
      { name: "Basic Understanding of AI", slug: "Basic Understanding of AI" },
      { name: "Basic Prompt Structure",    slug: "Basic Prompt Structure" },
      { name: "Core Skills to Practice",   slug: "Core Skills to Practice" },
      { name: "Basic Technical Awareness", slug: "Basic Technical Awareness" },
      { name: "Golden Rules (Gen AI)",     slug: "Golden Rules (Gen AI)" },
    ],
  },
];

// ── MOBILE BREAKPOINT HOOK ────────────────────────────────────────────────────
// This is the KEY fix — we use a React hook to detect screen width
// instead of relying on Tailwind classes fighting with inline styles
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return isMobile;
};

// ── HELPERS ───────────────────────────────────────────────────────────────────
const decodeHtml = (html) => {
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
};
const stripHtml = (html) => {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};
const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

// ── CATEGORY ITEM (recursive) ─────────────────────────────────────────────────
const CategoryItem = ({ item, depth = 0, activeSlug, onSelect }) => {
  const [isOpen, setIsOpen] = useState(true);
  const hasChildren = item.children && item.children.length > 0;
  const isActive = activeSlug === item.slug;

  return (
    <div style={{ marginBottom: 2 }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "6px 10px", borderRadius: 8, cursor: "pointer",
        background: isActive ? "#eff6ff" : "transparent",
        marginLeft: depth > 0 ? 12 : 0,
        transition: "background 0.15s",
      }}>
        <button
          onClick={() => onSelect(item.slug)}
          style={{
            background: "none", border: "none", cursor: "pointer", padding: 0,
            fontSize: depth > 0 ? 12 : 13,
            fontWeight: isActive ? 700 : depth === 0 ? 600 : 400,
            color: isActive ? S.blue : S.textMid,
            textAlign: "left", flex: 1,
            fontFamily: S.font,
          }}
        >
          {depth > 0 && <span style={{ color: S.textMuted, marginRight: 6 }}>›</span>}
          {item.name}
        </button>
        {hasChildren && (
          <button
            onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); }}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: S.textMuted, fontSize: 14, lineHeight: 1, padding: "0 4px",
            }}
          >
            {isOpen ? "−" : "+"}
          </button>
        )}
      </div>
      {hasChildren && isOpen && (
        <div style={{ borderLeft: `2px solid ${S.border}`, marginLeft: 14, paddingLeft: 2 }}>
          {item.children.map((child) => (
            <CategoryItem key={child.slug} item={child} depth={depth + 1}
              activeSlug={activeSlug} onSelect={onSelect} />
          ))}
        </div>
      )}
    </div>
  );
};

// ── HERO CARD (featured post) ─────────────────────────────────────────────────
const HeroCard = ({ post, onClick }) => {
  const img = post._embedded?.["wp:featuredmedia"]?.[0]?.source_url;
  const cat = post._embedded?.["wp:term"]?.[0]?.[0]?.name || "General";
  const title = decodeHtml(post.title.rendered);
  const excerpt = stripHtml(post.excerpt?.rendered || "").slice(0, 160) + "...";
  const author = post._embedded?.author?.[0]?.name || "MetaVix";

  return (
    <div
      onClick={() => onClick(post)}
      style={{
        borderRadius: 20, overflow: "hidden", cursor: "pointer",
        boxShadow: "0 8px 32px rgba(30,58,138,0.12)",
        background: S.card, border: `1px solid ${S.border}`,
        display: "flex", flexDirection: "column",
        transition: "transform 0.2s, box-shadow 0.2s",
        marginBottom: 32,
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 16px 48px rgba(30,58,138,0.18)"; }}
      onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 8px 32px rgba(30,58,138,0.12)"; }}
    >
      {/* Image */}
      <div style={{ position: "relative", height: 320, overflow: "hidden", background: S.bg }}>
        {img ? (
          <img src={img} alt={title}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        ) : (
          <div style={{ width: "100%", height: "100%", background: S.gradientBg,
            display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: 64, opacity: 0.3 }}>📰</span>
          </div>
        )}
        {/* Gradient overlay */}
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(to top, rgba(30,58,138,0.7) 0%, transparent 60%)",
        }}/>
        {/* Featured badge */}
        <div style={{
          position: "absolute", top: 16, left: 16,
          background: S.red, color: "#fff",
          fontSize: 10, fontWeight: 700, letterSpacing: "0.1em",
          textTransform: "uppercase", padding: "4px 12px", borderRadius: 20,
          fontFamily: S.font,
        }}>
          Featured
        </div>
        {/* Category badge */}
        <div style={{
          position: "absolute", top: 16, left: 90,
          background: "rgba(255,255,255,0.15)", backdropFilter: "blur(8px)",
          color: "#fff", fontSize: 10, fontWeight: 600,
          letterSpacing: "0.08em", textTransform: "uppercase",
          padding: "4px 12px", borderRadius: 20, border: "1px solid rgba(255,255,255,0.25)",
          fontFamily: S.font,
        }}>
          {cat}
        </div>
        {/* Title overlay on image */}
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "24px 28px" }}>
          <h2 style={{
            fontSize: 22, fontWeight: 800, color: "#fff", lineHeight: 1.3, margin: 0,
            textShadow: "0 2px 8px rgba(0,0,0,0.3)",
          }}>
            {title}
          </h2>
        </div>
      </div>
      {/* Content */}
      <div style={{ padding: "20px 28px 24px" }}>
        <p style={{ fontSize: 15, color: "#374151", lineHeight: 1.8, margin: "0 0 16px" }}>
          {excerpt}
        </p>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <span style={{ fontSize: 12, color: S.textMuted }}>📅 {formatDate(post.date)}</span>
            <span style={{ fontSize: 12, color: S.textMuted }}>✍️ {author}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, color: S.red, fontSize: 13, fontWeight: 700 }}>
            Read Article
            <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14m-7-7 7 7-7 7" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── ARTICLE CARD (grid) ───────────────────────────────────────────────────────
const ArticleCard = ({ post, onClick }) => {
  const img = post._embedded?.["wp:featuredmedia"]?.[0]?.source_url;
  const cat = post._embedded?.["wp:term"]?.[0]?.[0]?.name || "General";
  const title = decodeHtml(post.title.rendered);
  const excerpt = stripHtml(post.excerpt?.rendered || "").slice(0, 100) + "...";
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onClick={() => onClick(post)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderRadius: 16, overflow: "hidden", cursor: "pointer",
        background: S.card,
        border: `1px solid ${hovered ? S.blue : S.border}`,
        boxShadow: hovered ? "0 8px 24px rgba(30,58,138,0.12)" : "0 1px 4px rgba(0,0,0,0.05)",
        transition: "all 0.2s",
        transform: hovered ? "translateY(-2px)" : "translateY(0)",
        display: "flex", flexDirection: "column",
      }}
    >
      <div style={{ height: 180, overflow: "hidden", background: S.bg, position: "relative" }}>
        {img ? (
          <img src={img} alt={title}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block",
              transform: hovered ? "scale(1.04)" : "scale(1)", transition: "transform 0.3s" }} />
        ) : (
          <div style={{ width: "100%", height: "100%", background: S.gradientBg,
            display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: 40, opacity: 0.25 }}>📄</span>
          </div>
        )}
        <div style={{
          position: "absolute", top: 12, left: 12,
          background: "#eff6ff", color: S.blue,
          fontSize: 10, fontWeight: 700, letterSpacing: "0.08em",
          textTransform: "uppercase", padding: "3px 10px", borderRadius: 20,
        }}>
          {cat}
        </div>
      </div>
      <div style={{ padding: "16px 18px 18px", flex: 1, display: "flex", flexDirection: "column" }}>
        <h3 style={{
          fontSize: 14, fontWeight: 700, color: hovered ? "#1d4ed8" : "#1e3a8a",
          lineHeight: 1.45, margin: "0 0 8px", transition: "color 0.2s",
        }}>
          {title}
        </h3>
        <p style={{ fontSize: 13, color: "#374151", lineHeight: 1.8, margin: "0 0 14px", flex: 1 }}>
          {excerpt}
        </p>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 11, color: S.textMuted }}>📅 {formatDate(post.date)}</span>
          <div style={{ display: "flex", alignItems: "center", gap: 4, color: S.red, fontSize: 11, fontWeight: 700 }}>
            Read more
            <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14m-7-7 7 7-7 7" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── ARTICLE DETAIL ────────────────────────────────────────────────────────────
const ArticleDetail = ({ post, onBack }) => {
  const img = post._embedded?.["wp:featuredmedia"]?.[0]?.source_url;
  const cat = post._embedded?.["wp:term"]?.[0]?.[0]?.name || "General";
  const title = decodeHtml(post.title.rendered);
  const author = post._embedded?.author?.[0]?.name || "MetaVix";

  return (
    <div style={{
      background: S.card, border: `1px solid ${S.border}`,
      borderRadius: 20, overflow: "hidden",
      boxShadow: "0 4px 24px rgba(30,58,138,0.08)",
    }}>
      <div style={{ padding: "16px 20px", borderBottom: `1px solid ${S.border}`, background: S.bg }}>
        <button onClick={onBack} style={{
          display: "flex", alignItems: "center", gap: 8,
          background: "none", border: "none", cursor: "pointer",
          color: S.blue, fontSize: 13, fontWeight: 700, padding: 0,
        }}>
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 12H5m7-7-7 7 7 7" />
          </svg>
          Back to Blog
        </button>
      </div>
      {img && (
        <div style={{ height: 240, overflow: "hidden" }}>
          <img src={img} alt={title}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        </div>
      )}
      <div style={{ padding: "24px 20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
          <span style={{
            background: "#eff6ff", color: S.blue, fontSize: 11, fontWeight: 700,
            letterSpacing: "0.08em", textTransform: "uppercase", padding: "4px 12px", borderRadius: 20,
          }}>
            {cat}
          </span>
          <span style={{ fontSize: 12, color: S.textMuted }}>📅 {formatDate(post.date)}</span>
          <span style={{ fontSize: 12, color: S.textMuted }}>✍️ {author}</span>
        </div>
        <h1 style={{ fontSize: "clamp(1.3rem, 4vw, 1.8rem)", fontWeight: 800, color: "#1e3a8a", lineHeight: 1.3, margin: "0 0 20px" }}>
          {title}
        </h1>
        <div style={{ height: 3, width: 48, background: S.gradientBg, borderRadius: 2, marginBottom: 24 }} />
        <div
          style={{ fontSize: 15, color: "#374151", lineHeight: 1.8 }}
          dangerouslySetInnerHTML={{ __html: post.content.rendered }}
        />
      </div>
    </div>
  );
};

// ── LOADING SKELETON ──────────────────────────────────────────────────────────
const LoadingSkeleton = () => (
  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 20 }}>
    {[1,2,3,4,5,6].map(i => (
      <div key={i} style={{ borderRadius: 16, overflow: "hidden", background: S.card, border: `1px solid ${S.border}` }}>
        <div style={{ height: 180, background: "#f1f5f9", animation: "pulse 1.5s ease-in-out infinite" }} />
        <div style={{ padding: 18 }}>
          {[40, 85, 100, 65].map((w, j) => (
            <div key={j} style={{ height: j === 1 ? 14 : 12, background: "#f1f5f9", borderRadius: 4,
              width: `${w}%`, marginBottom: 8, animation: "pulse 1.5s ease-in-out infinite" }} />
          ))}
        </div>
      </div>
    ))}
    <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }`}</style>
  </div>
);

// ── MAIN BLOG PAGE ────────────────────────────────────────────────────────────
const Blog = () => {
  const isMobile = useIsMobile(); // ← THE KEY FIX

  const [posts,              setPosts]             = useState([]);
  const [loading,            setLoading]           = useState(true);
  const [error,              setError]             = useState(null);
  const [selectedPost,       setSelectedPost]      = useState(null);
  const [activeCategory,     setActiveCategory]    = useState(null);
  const [activeCategoryName, setActiveCategoryName]= useState("");
  const [searchQuery,        setSearchQuery]       = useState("");
  const [searchInput,        setSearchInput]       = useState("");
  const [wpCategories,       setWpCategories]      = useState([]);

  useEffect(() => {
    fetch(`${WP_API}/categories?per_page=100`)
      .then(r => r.json())
      .then(d => setWpCategories(d))
      .catch(e => console.error("Failed to fetch categories", e));
  }, []);

  useEffect(() => {
    setLoading(true); setError(null); setPosts([]);
    const matchedCat = wpCategories.find(c => c.slug === activeCategory);
    const catId = matchedCat ? matchedCat.id : null;
    let url = `${WP_API}/posts?_embed&per_page=20&orderby=date&order=desc`;
    if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
    if (catId)       url += `&categories=${catId}`;
    fetch(url)
      .then(r => { if (!r.ok) throw new Error("Failed"); return r.json(); })
      .then(d => { setPosts(d); setLoading(false); })
      .catch(e => { console.error(e); setError("Unable to load articles. Please check your WordPress connection."); setLoading(false); });
  }, [searchQuery, activeCategory, wpCategories]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
    setActiveCategory(null); setActiveCategoryName("");
    setSelectedPost(null);
  };

  const handleCategorySelect = (slug, name) => {
    if (slug === activeCategory) { setActiveCategory(null); setActiveCategoryName(""); }
    else { setActiveCategory(slug); setActiveCategoryName(name); }
    setSearchQuery(""); setSearchInput(""); setSelectedPost(null);
  };

  const findName = (cats, s) => {
    for (const c of cats) {
      if (c.slug === s) return c.name;
      if (c.children) { const f = findName(c.children, s); if (f) return f; }
    }
    return s;
  };

  const heroPost  = posts[0] || null;
  const gridPosts = posts.slice(1);

  return (
    <div style={{ minHeight: "100vh", background: S.bg }}>

      {/* ── HERO BANNER ── */}
      <section style={{
        background: S.gradientBg,
        color: "#fff",
        padding: "40px 20px",
        textAlign: "center",
      }}>
        <h1 style={{
          fontSize: "clamp(1.5rem, 4vw, 2.2rem)",
          fontWeight: 800,
          marginBottom: 12,
          fontFamily: S.font,
          lineHeight: 1.3,
          padding: "0 16px",
        }}>
          Oracle ERP, Analytics &amp; AI Insights
        </h1>
        <p style={{
          color: "rgba(191,219,254,0.9)",
          fontSize: "clamp(0.85rem, 2vw, 1rem)",
          maxWidth: 600,
          margin: "0 auto",
          padding: "0 16px",
          lineHeight: 1.6,
        }}>
          Technical insights and updates for Enterprise Professionals
        </p>
      </section>

      {/* ── CATEGORY PILLS + SEARCH ── */}
      <div style={{
        background: "#fff", borderBottom: `1px solid ${S.border}`,
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
      }}>
        <div style={{
          maxWidth: 1280, margin: "0 auto", padding: "10px 16px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 10, flexWrap: "wrap",
        }}>
          {/* Pills */}
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            overflowX: "auto", scrollbarWidth: "none", flex: 1,
          }}>
            <button
              onClick={() => { setActiveCategory(null); setActiveCategoryName(""); setSearchQuery(""); setSearchInput(""); }}
              style={{
                padding: "6px 16px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                cursor: "pointer", whiteSpace: "nowrap",
                border: `1.5px solid ${!activeCategory ? S.blue : S.border}`,
                background: !activeCategory ? S.blue : "#fff",
                color: !activeCategory ? "#fff" : S.textLight,
                transition: "all 0.15s",
              }}
            >
              All Articles
            </button>
            {sidebarCategories.map(cat => (
              <button
                key={cat.slug}
                onClick={() => handleCategorySelect(cat.slug, cat.name)}
                style={{
                  padding: "6px 16px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                  cursor: "pointer", whiteSpace: "nowrap",
                  border: `1.5px solid ${activeCategory === cat.slug ? S.blue : S.border}`,
                  background: activeCategory === cat.slug ? S.blue : "#fff",
                  color: activeCategory === cat.slug ? "#fff" : S.textLight,
                  transition: "all 0.15s",
                }}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {/* Search */}
          <form onSubmit={handleSearch} style={{
            display: "flex", alignItems: "center",
            border: `1.5px solid ${S.border}`, borderRadius: 8,
            overflow: "hidden", background: "#fff",
            flexShrink: 0,
            width: isMobile ? "100%" : "auto",
            maxWidth: isMobile ? "100%" : 300,
          }}>
            <div style={{ padding: "0 10px", display: "flex", alignItems: "center" }}>
              <svg width="14" height="14" fill="none" viewBox="0 0 24 24"
                stroke={S.textMuted} strokeWidth={2.5}>
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              style={{
                border: "none", outline: "none", padding: "7px 0",
                fontSize: 13, color: S.textDark, background: "transparent",
                fontFamily: S.font, flex: 1,
              }}
            />
            <button type="submit" style={{
              padding: "7px 14px", background: S.red, color: "#fff",
              border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700,
            }}>
              Go
            </button>
          </form>
        </div>
      </div>

      {/* ── MAIN CONTENT ── */}
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "24px 16px" }}>
        <div style={{
          display: "flex",
          gap: 28,
          alignItems: "flex-start",
          // On mobile: column (stack), on desktop: row (side by side)
          flexDirection: isMobile ? "column" : "row",
        }}>

          {/* ── LEFT: Articles ── */}
          <main style={{ flex: 1, minWidth: 0, width: "100%" }}>
            {selectedPost ? (
              <ArticleDetail post={selectedPost} onBack={() => setSelectedPost(null)} />
            ) : (
              <>
                {/* Section label */}
                <div style={{
                  display: "flex", alignItems: "center",
                  justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 8,
                }}>
                  <div>
                    <p style={{
                      fontSize: 11, fontWeight: 700, letterSpacing: "0.1em",
                      textTransform: "uppercase", color: S.blue, margin: "0 0 4px",
                      fontFamily: S.font,
                    }}>
                      {searchQuery ? `Search: "${searchQuery}"` : activeCategory ? `Category: ${activeCategoryName}` : "Latest"}
                    </p>
                    <h2 style={{ fontSize: 22, fontWeight: 800, color: "#1e3a8a", margin: 0 }}>
                      {searchQuery ? "Search Results" : activeCategory ? activeCategoryName : "All Articles"}
                    </h2>
                  </div>
                  {activeCategory && (
                    <button
                      onClick={() => { setActiveCategory(null); setActiveCategoryName(""); }}
                      style={{
                        background: "#fef2f2", border: `1px solid #fecaca`,
                        color: S.red, fontSize: 12, fontWeight: 600,
                        padding: "6px 14px", borderRadius: 8, cursor: "pointer",
                        fontFamily: S.font,
                      }}
                    >
                      ✕ Clear filter
                    </button>
                  )}
                </div>

                {loading && <LoadingSkeleton />}

                {error && (
                  <div style={{
                    padding: 28, background: "#fef2f2", border: "1px solid #fecaca",
                    borderRadius: 16, textAlign: "center",
                  }}>
                    <p style={{ color: S.red, fontWeight: 700, marginBottom: 8 }}>⚠️ Connection Error</p>
                    <p style={{ color: "#ef4444", fontSize: 13, marginBottom: 12 }}>{error}</p>
                    <p style={{ color: S.textLight, fontSize: 12 }}>
                      Make sure WordPress is installed at <strong>cms.metavixsolutions.com</strong> and the REST API is enabled.
                    </p>
                  </div>
                )}

                {!loading && !error && posts.length === 0 && (
                  <div style={{
                    padding: 48, background: S.card, border: `1px solid ${S.border}`,
                    borderRadius: 16, textAlign: "center",
                  }}>
                    <div style={{ fontSize: 48, marginBottom: 16 }}>📭</div>
                    <p style={{ fontSize: 16, fontWeight: 700, color: S.textDark, marginBottom: 8 }}>
                      No articles found
                    </p>
                    <p style={{ fontSize: 13, color: S.textLight }}>
                      {activeCategory ? `No posts in "${activeCategoryName}" yet.` : "No articles published yet."}
                    </p>
                  </div>
                )}

                {!loading && !error && posts.length > 0 && (
                  <>
                    {heroPost && <HeroCard post={heroPost} onClick={setSelectedPost} />}
                    {gridPosts.length > 0 && (
                      <>
                        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                          <div style={{ height: 2, width: 32, background: S.gradientBg, borderRadius: 1 }} />
                          <span style={{ fontSize: 12, fontWeight: 700, color: S.textLight,
                            letterSpacing: "0.08em", textTransform: "uppercase" }}>
                            More Articles
                          </span>
                        </div>
                        <div style={{
                          display: "grid",
                          gridTemplateColumns: isMobile
                            ? "1fr"                              // 1 column on mobile
                            : "repeat(auto-fill,minmax(260px,1fr))", // auto on desktop
                          gap: 20,
                        }}>
                          {gridPosts.map(post => (
                            <ArticleCard key={post.id} post={post} onClick={setSelectedPost} />
                          ))}
                        </div>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </main>

          {/* ── RIGHT: Sidebar — ONLY shown on desktop ── */}
          {!isMobile && (
            <aside style={{
              width: 268,
              flexShrink: 0,
              position: "sticky",
              top: 24,
              display: "flex",
              flexDirection: "column",
              gap: 16,
            }}>
              {/* Browse Topics */}
              <div style={{
                background: S.card, border: `1px solid ${S.border}`,
                borderRadius: 16, overflow: "hidden",
                boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
              }}>
                <div style={{
                  padding: "14px 18px", borderBottom: `1px solid ${S.border}`,
                  background: S.gradientBg,
                }}>
                  <h3 style={{
                    fontSize: 13, fontWeight: 700, color: "#fff",
                    margin: 0, display: "flex", alignItems: "center", gap: 8,
                    fontFamily: S.font,
                  }}>
                    <svg width="14" height="14" fill="none" viewBox="0 0 24 24"
                      stroke="currentColor" strokeWidth={2}>
                      <rect x="3" y="3" width="7" height="7" rx="1"/>
                      <rect x="14" y="3" width="7" height="7" rx="1"/>
                      <rect x="14" y="14" width="7" height="7" rx="1"/>
                      <rect x="3" y="14" width="7" height="7" rx="1"/>
                    </svg>
                    Browse Topics
                  </h3>
                </div>
                <nav style={{ padding: "12px 12px" }}>
                  {sidebarCategories.map(cat => (
                    <CategoryItem
                      key={cat.slug}
                      item={cat}
                      activeSlug={activeCategory}
                      onSelect={(slug) => handleCategorySelect(slug, findName(sidebarCategories, slug))}
                    />
                  ))}
                </nav>
              </div>

              {/* Article count */}
              {!loading && !error && posts.length > 0 && (
                <div style={{
                  background: S.gradientBg, borderRadius: 16,
                  padding: "20px", textAlign: "center",
                  boxShadow: "0 4px 16px rgba(30,58,138,0.2)",
                }}>
                  <div style={{ fontSize: 36, fontWeight: 800, color: "#fff", fontFamily: S.font, lineHeight: 1 }}>
                    {posts.length}
                  </div>
                  <div style={{
                    fontSize: 10, color: "rgba(191,219,254,0.9)",
                    fontWeight: 700, letterSpacing: "0.1em",
                    textTransform: "uppercase", marginTop: 6,
                  }}>
                    {activeCategory ? "Articles in Category" : "Articles Published"}
                  </div>
                </div>
              )}

              {/* Quick tip card */}
              <div style={{
                background: "#fff", border: `1px solid ${S.border}`,
                borderRadius: 16, padding: "18px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10,
                  background: "#eff6ff", display: "flex",
                  alignItems: "center", justifyContent: "center",
                  fontSize: 18, marginBottom: 10,
                }}>
                  💡
                </div>
                <p style={{ fontSize: 12, fontWeight: 700, color: S.textDark, margin: "0 0 6px" }}>
                  Stay Updated
                </p>
                <p style={{ fontSize: 11, color: S.textLight, lineHeight: 1.6, margin: 0 }}>
                  Browse topics from the sidebar to filter articles by Oracle Cloud, Analytics, or AI categories.
                </p>
              </div>
            </aside>
          )}

        </div>
      </div>
    </div>
  );
};

export default Blog;